import React, { useState, useEffect } from 'react'
import { IAttendee, IEpiProps, IRenewalEligibilityResponse } from '../models/event-reg-refresh-models'
import { LoadingSpinner } from './loading-spinner'
import { toProperCaseName } from '@/lib/string/name-formatting'

// Autofill customer name fields based on email lookup
const autofillCustomerNameByEmail = async (
  email: string,
  currentData: IAttendee,
  onLoadingStart?: () => void,
  onLoadingEnd?: () => void
): Promise<IAttendee> => {
  // Only autofill if either firstName OR lastName is empty (but not if both are filled)
  if (currentData.firstName.trim() && currentData.lastName.trim()) {
    return currentData
  }

  // Signal loading start
  if (onLoadingStart) {
    onLoadingStart()
  }

  try {
    const response = await fetch(`/internalapi/EventRegistrationRefreshApi/GetCustomerByEmail?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })

    if (response.ok) {
      const customerData = await response.json()
      
      if (customerData.found) {
        // Only update empty fields - preserve any manually entered data
        return {
          ...currentData,
          firstName: currentData.firstName.trim() ? currentData.firstName : toProperCaseName(customerData.firstName),
          lastName: currentData.lastName.trim() ? currentData.lastName : toProperCaseName(customerData.lastName)
        }
      }
    }
  } catch (error) {
    // Silently fail - autofill is a nice-to-have feature
    console.log('Could not autofill customer information:', error)
  } finally {
    // Always signal loading end
    if (onLoadingEnd) {
      onLoadingEnd()
    }
  }

  // Return original data if no autofill occurred
  return currentData
}

interface IAttendeeFormProps {
  onChange: (attendee: IAttendee) => void
  onBlur?: (attendee: IAttendee) => void
  onEligibilityCheck?: (attendee: IAttendee, isEligible: boolean) => void
  onRemove?: () => void
  showRemoveButton?: boolean
  attendee?: IAttendee
  attendeeIndex: number
  errors?: Partial<IAttendee>
  isDuplicateEmail?: boolean
  epiProps: IEpiProps
  isRenewalProgram?: boolean
  specialtyCode?: string
}

export const AttendeeForm = ({ onChange, onBlur, onEligibilityCheck, onRemove, showRemoveButton, attendee, attendeeIndex, errors, isDuplicateEmail, epiProps, isRenewalProgram, specialtyCode }: IAttendeeFormProps) => {
  const [formData, setFormData] = useState<IAttendee>({
    firstName: attendee?.firstName || '',
    lastName: attendee?.lastName || '',
    email: attendee?.email || '',
    title: attendee?.title || '',
    isEligible: attendee?.isEligible,
    eligibilityChecked: attendee?.eligibilityChecked
  })
  const [eligibilityError, setEligibilityError] = useState<string | null>(null)
  const [isAutofillingName, setIsAutofillingName] = useState(false)

  // Check if any field has an error
  const hasAnyError = !!(
    errors?.firstName || 
    errors?.lastName || 
    errors?.email || 
    errors?.title ||
    isDuplicateEmail ||
    eligibilityError
  )

  // Update parent whenever form data changes
  useEffect(() => {
    onChange(formData)
  }, [formData])

  const handleInputChange = (field: keyof IAttendee, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleNameBlur = (field: 'firstName' | 'lastName') => {
    setFormData(prev => {
      const value = prev[field]
      if (!value) return prev
      return { ...prev, [field]: toProperCaseName(value) }
    })
  }

  const handleInputBlur = () => {
    if (onBlur) {
      onBlur(formData)
    }
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email)
  }

  const handleEmailBlur = async () => {
    // Always call the regular blur handler
    handleInputBlur()

    // Clear previous eligibility status when email changes
    if (formData.email !== attendee?.email) {
      setEligibilityError(null)
      const updatedData = { ...formData, isEligible: undefined, eligibilityChecked: false }
      setFormData(updatedData)
      onChange(updatedData)
    }

    // Validate email format before any checks
    if (!formData.email.trim() || !validateEmail(formData.email)) {
      return
    }

    // Autofill customer name fields if needed
    const autofilledData = await autofillCustomerNameByEmail(
      formData.email, 
      formData,
      () => setIsAutofillingName(true),
      () => setIsAutofillingName(false)
    )
    if (autofilledData !== formData) {
      setFormData(autofilledData)
      onChange(autofilledData)
    }
  }

  return (
    <div className="attendee-form">
      <div className="attendee-form-header">
        <div className="attendee-form-header-left">
          <h3 className="attendee-form-title">
            {epiProps.attendeeFormTitleFormat.replace('{0}', (attendeeIndex + 1).toString())}
          </h3>
          {hasAnyError && (
            <svg className="attendee-form-error-icon" aria-label="Form has errors" aria-hidden="true" focusable="false">
              <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#icon-attendee-error" />
            </svg>
          )}
        </div>
        {showRemoveButton && (
          <button 
            className="btn-tertiary btn-remove"
            onClick={onRemove}
            aria-label={`Remove ${formData.firstName || 'Attendee'} ${formData.lastName || (attendeeIndex + 1)}`}
            type="button"
          >
            <svg className="svg-icon" aria-hidden="true" focusable="false">
              <use xmlnsXlink="http://www.w3.org/2000/xlink" xlinkHref="#trash-icon" />
            </svg>
          </button>
        )}
      </div>
      
      <div className="attendee-form-row">
        <div className="attendee-form-field">
          <label htmlFor={`firstName-${attendeeIndex}`}>
            {epiProps.firstNameLabel}
            <span className={`field-requirement ${errors?.firstName ? 'error' : ''}`}>{epiProps.fieldRequirementText}</span>
          </label>
          <div className="attendee-form-field-input-wrapper">
            <input
              id={`firstName-${attendeeIndex}`}
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              onBlur={() => { handleNameBlur('firstName'); handleInputBlur() }}
              className={`${errors?.firstName ? 'error' : ''} ${isAutofillingName && !formData.firstName.trim() ? 'with-spinner' : ''}`}
              placeholder={epiProps.firstNamePlaceholder}
            />
            {isAutofillingName && !formData.firstName.trim() && (
              <LoadingSpinner className="attendee-form-field-spinner" />
            )}
          </div>
          {errors?.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>
        
        <div className="attendee-form-field">
          <label htmlFor={`lastName-${attendeeIndex}`}>
            {epiProps.lastNameLabel}
            <span className={`field-requirement ${errors?.lastName ? 'error' : ''}`}>{epiProps.fieldRequirementText}</span>
          </label>
          <div className="attendee-form-field-input-wrapper">
            <input
              id={`lastName-${attendeeIndex}`}
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              onBlur={() => { handleNameBlur('lastName'); handleInputBlur() }}
              className={`${errors?.lastName ? 'error' : ''} ${isAutofillingName && !formData.lastName.trim() ? 'with-spinner' : ''}`}
              placeholder={epiProps.lastNamePlaceholder}
            />
            {isAutofillingName && !formData.lastName.trim() && (
              <LoadingSpinner className="attendee-form-field-spinner" />
            )}
          </div>
          {errors?.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>
      </div>

      <div className="attendee-form-row">
        <div className="attendee-form-field">
          <label htmlFor={`email-${attendeeIndex}`}>
            {epiProps.workEmailLabel}
            <span className={`field-requirement ${errors?.email || isDuplicateEmail || eligibilityError ? 'error' : ''}`}>{epiProps.fieldRequirementText}</span>
          </label>
          <input
            id={`email-${attendeeIndex}`}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={handleEmailBlur}
            className={errors?.email || eligibilityError ? 'error' : ''}
            placeholder={epiProps.emailPlaceholder}
            disabled={false}
          />
          {errors?.email && <span className="error-message" dangerouslySetInnerHTML={{ __html: errors.email }} />}
          {!errors?.email && isDuplicateEmail && epiProps.duplicateEmailFieldErrorMessage && (
            <span className="error-message">{epiProps.duplicateEmailFieldErrorMessage}</span>
          )}
          {eligibilityError && !errors?.email && !isDuplicateEmail && (
            <span className="error-message" dangerouslySetInnerHTML={{ __html: eligibilityError }} />
          )}
        </div>

        <div className="attendee-form-field">
          <label htmlFor={`title-${attendeeIndex}`}>
            {epiProps.titleLabel}
            <span className={`field-requirement ${errors?.title ? 'error' : ''}`}>{epiProps.fieldRequirementText}</span>
          </label>
          <input
            id={`title-${attendeeIndex}`}
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            onBlur={handleInputBlur}
            className={errors?.title ? 'error' : ''}
            placeholder={epiProps.titlePlaceholder}
          />
          {errors?.title && <span className="error-message">{errors.title}</span>}
        </div>
      </div>
    </div>
  )
}